/**
 * Chained dual-column scroll for the PDP hero.
 *
 * On desktop the PDP shows two columns: the gallery (left) and the info block
 * (right). This mixin intercepts the mouse wheel over the hero and routes the
 * scroll to the column the cursor is over. When that column reaches its edge,
 * the remaining delta is chained to the other column. When both columns are at
 * the edge in the wheel direction, the event is left untouched so the page
 * scrolls normally (recommendations/footer below, or above the PDP). This makes
 * the behaviour naturally reversible/symmetric without tracking a "completed"
 * state.
 *
 * Scope: desktop only, mouse-wheel driven. It only activates when
 * `isBreakpointDesktop` is true (>= bp_575, provided by windowResizeMixin) and
 * hooks the `wheel` event, so touch devices (which emit `touchmove`, not
 * `wheel`) keep the native scroll untouched.
 *
 * Requirements of the host component:
 * - windowResizeMixin (exposes `isBreakpointDesktop`).
 * - `ref="imagesContainer"` on the left column and `ref="infoContainer"` on
 *   the right column.
 * - `isZoomOpen` data property (true while the image zoom overlay is open).
 */

// Tolerance in px to consider a column "at the bottom" despite subpixel rounding
const EDGE_TOLERANCE = 1;
// Tolerance in px to consider the hero "docked" (fully covering the viewport
// below the sticky header) despite subpixel rounding and header-height jitter.
const DOCK_TOLERANCE = 2;
const HERO_SELECTOR = '.product-detail';

export default {
  data: () => ({
    pdpDualScrollHero: null,
    pdpDualScrollActive: false,
  }),

  watch: {
    isBreakpointDesktop(isDesktop) {
      if (isDesktop) {
        this.enablePdpDualScroll();
      } else {
        this.disablePdpDualScroll();
      }
    },
  },

  mounted() {
    if (this.isBreakpointDesktop) {
      this.enablePdpDualScroll();
    }
  },

  beforeDestroy() {
    this.disablePdpDualScroll();
  },

  methods: {
    enablePdpDualScroll() {
      if (this.pdpDualScrollActive) return;
      this.pdpDualScrollHero = this.$el?.querySelector?.(HERO_SELECTOR) || null;
      if (!this.pdpDualScrollHero) return;
      this.pdpDualScrollHero.addEventListener(
        'wheel',
        this.handlePdpDualScrollWheel,
        { passive: false }
      );
      this.pdpDualScrollActive = true;
    },

    disablePdpDualScroll() {
      if (!this.pdpDualScrollActive) return;
      this.pdpDualScrollHero?.removeEventListener(
        'wheel',
        this.handlePdpDualScrollWheel,
        { passive: false }
      );
      this.pdpDualScrollHero = null;
      this.pdpDualScrollActive = false;
    },

    /**
     * Sticky-header offset in px (top edge of the docked columns).
     * Read from the inline `--header-total-height` custom property set by
     * header.js — a cheap read that avoids a reflow.
     * @returns {number} - header offset in px (0 if unavailable)
     */
    pdpHeaderOffset() {
      const raw = document.documentElement.style.getPropertyValue(
        '--header-total-height'
      );
      return parseFloat(raw) || 0;
    },

    /**
     * Whether the hero fully covers the scroll viewport below the header.
     * @returns {boolean} - true if the hero is docked
     */
    isPdpHeroDocked() {
      const hero = this.pdpDualScrollHero;
      if (!hero) return false;
      const rect = hero.getBoundingClientRect();
      return (
        rect.top <= this.pdpHeaderOffset() + DOCK_TOLERANCE &&
        rect.bottom >= window.innerHeight - DOCK_TOLERANCE
      );
    },

    /**
     * Whether the given element can still scroll in the wheel direction.
     * @param {HTMLElement} el - scrollable column
     * @param {number} delta - wheel deltaY (negative = up, positive = down)
     * @returns {boolean} - true if the column can still scroll in that direction
     */
    canPdpColumnScroll(el, delta) {
      if (!el) return false;
      if (delta < 0) {
        return el.scrollTop > 0;
      }
      return el.scrollTop + el.clientHeight < el.scrollHeight - EDGE_TOLERANCE;
    },

    handlePdpDualScrollWheel(event) {
      if (this.isZoomOpen) return;

      const delta = event.deltaY;
      if (!delta) return;

      // Only route the wheel between columns while the hero is docked; otherwise
      // the pinned column eats the scroll and the page can't settle the hero.
      if (!this.isPdpHeroDocked()) return;

      const images = this.$refs.imagesContainer;
      const info = this.$refs.infoContainer;
      if (!images || !info) return;

      const overImages = images.contains(event.target);
      const active = overImages ? images : info;
      const other = overImages ? info : images;

      if (this.canPdpColumnScroll(active, delta)) {
        event.preventDefault();
        active.scrollTop += delta;
      } else if (this.canPdpColumnScroll(other, delta)) {
        event.preventDefault();
        other.scrollTop += delta;
      }
      // Both columns at the edge: let the page scroll (release)
    },
  },
};
